import React from "react";
import { MentionsInput, Mention } from "react-mentions";
import { HiOutlineX } from "react-icons/hi";
import { MdOutlineAttachment } from "react-icons/md";
import { isEmpty, MAX_ESCALATION_LIMIT, ESCALATION_OPTIONS } from "../hooks/constants";
import defaultStyle from "@components/MarketVisitReport/defaultStyle";
import defaultMentionStyle from "@components/MarketVisitReport/defaultMentionStyle";
import styles from "@components/MarketVisitReport/MarketVisitReport.module.css";

/**
 * EscalationSection Component
 * Handles escalation rows for local issues with dropdown, text input, and file attachments
 * 
 * @param {Array} rows - Escalation rows data
 * @param {Function} handleInputChange - Handler for row field changes
 * @param {Function} addRow - Handler to add new row
 * @param {Function} removeRow - Handler to remove row
 * @param {Function} handleFileChange - Handler for file uploads
 * @param {Array} productList - List of products for mentions
 * @param {Array} wssList - List of WSS for mentions
 * @param {Array} dealerList - List of dealers for mentions
 * @param {Object} characterLimitValidation - Character limit validation errors
 * @param {Object} formValidation - Form validation errors
 * @param {Boolean} disable - Form disable state
 * @param {Object} formData - Form data (for marketVisitDate key)
 */
const EscalationSection = ({
  rows,
  handleInputChange,
  addRow,
  removeRow,
  handleFileChange,
  productList,
  wssList,
  dealerList,
  characterLimitValidation,
  formValidation,
  disable,
  formData,
}) => {
  return (
    <>
      {/* Section Header */}
      <div className={`row m-0 ${styles.MarketVisitReport__headingBox}`}>
        <div className={`col-12 p-0 ${styles.MarketVisitReport__heading}`}>
          Escalations/Immediate Support Required
        </div>
      </div>

      <div className={styles.MarketVisitReport__selectableRows}>
        <label className={styles.MarketVisitReport__selectLabel}>
          Local Issues: Issues which are specific to a TTY/WSS/Dealer/TSI- Auto
          SR will be raised.
        </label>

        {rows.map((row, index) => (
          <div key={row.srId}>
            <p className="d-none">{row.srId}</p>

            {/* Remove Button - show for all rows except first when form is not disabled */}
            {index !== 0 && !disable && (
              <HiOutlineX
                style={{ color: "#ef5464" }}
                className={styles.MarketVisitReport__removeButton}
                onClick={() => removeRow(index)}
              />
            )}

            {/* Escalate To Dropdown */}
            <select
              value={row.assigned_to}
              className={styles.MarketVisitReport__selection}
              onChange={(e) =>
                handleInputChange(index, "assigned_to", e.target.value)
              }
              disabled={disable}
            >
              <option
                className={styles.MarketVisitReport__selectOptions}
                value=""
              >
                Escalate To: (Select From dropdown)
              </option>
              {ESCALATION_OPTIONS.map((option, i) => (
                <option
                  key={i}
                  className={styles.MarketVisitReport__selectOptions}
                  value={option}
                >
                  {option}
                </option>
              ))}
            </select>

            {/* Validation Error for Dropdown */}
            {formValidation[`rows.${index}.assigned_to`] && (
              <p
                className={`mb-2 ${styles.MarketVisitReport__validationErrors}`}
              >
                {formValidation[`rows.${index}.assigned_to`]}
              </p>
            )}

            {/* Min Characters Message */}
            <p className={styles.MarketVisitReport__selectAlertMsg}>
              {" "}
              *Min 20 characters input needed for service request creation{" "}
            </p>

            {/* Issue Text Input with Mentions */}
            <MentionsInput
              className={styles.MarketVisitRepor__input}
              value={isEmpty(row.issue) ? "" : row.issue}
              maxLength={2001}
              onChange={(e) =>
                handleInputChange(index, "issue", e.target.value)
              }
              style={defaultStyle}
              disabled={disable}
            >
              <Mention
                trigger="@p"
                data={productList}
                style={defaultMentionStyle}
              />
              <Mention trigger="@w" data={wssList} style={defaultMentionStyle} />
              <Mention
                trigger="@d"
                data={dealerList}
                style={defaultMentionStyle}
              />
            </MentionsInput>

            {/* Character Limit Validation */}
            {characterLimitValidation?.urgentEscalation && (
              <p className={styles.MarketVisitReport__validationErrors}>
                {characterLimitValidation?.urgentEscalation}
              </p>
            )}

            {/* Action Row: Add More Button, Selected Files, Attach File */}
            <div className={"row m-0"}>
              {/* Add More Escalation Button */}
              <div className="col-5 d-flex p-0">
                {index === rows.length - 1 &&
                  rows.length < MAX_ESCALATION_LIMIT &&
                  !disable && (
                    <button
                      className={`${styles.MarketVisitReport__addMore}`}
                      onClick={addRow}
                    >
                      {" "}
                      + Add more escalation
                    </button>
                  )}
              </div>

              {/* Selected Files Display */}
              <div className="col-3 p-0">
                {row.files.length > 0 && (
                  <div
                    className={`row ${styles.MarketVisitReport_selectedFiles}`}
                  >
                    {row.files.length === 1
                      ? `File: ${row.files[0].fileName}`
                      : `Selected Files: ${row.files.length}`}
                  </div>
                )}
              </div>

              {/* Attach File Input */}
              <div className="col-4 p-0">
                {!disable && (
                  <div
                    className={`${styles.MarketVisitReport__inputWrapper}`}
                  >
                    <MdOutlineAttachment
                      style={{
                        height: "1.5em",
                        width: "1.5em",
                        color: "#2cc26a",
                        marginRight: "5px",
                      }}
                    />
                    <label
                      className={styles.MarketVisitReport_fileInputLabel}
                    >
                      Attach File
                      <input
                        className={`${styles.MarketVisitReport_fileInput}`}
                        type="file"
                        accept=".jpg,.png,.pdf, .jpeg"
                        multiple
                        key={formData.marketVisitDate}
                        onChange={(e) => handleFileChange(index, e)}
                        disabled={disable}
                      />
                    </label>
                  </div>
                )}
              </div>
            </div>

            {/* File Validation Error */}
            {row.validationError?.file && (
              <p className={styles.MarketVisitReport__validationErrors}>
                {row.validationError.file}
              </p>
            )}
          </div>
        ))}
      </div>
    </>
  );
};

export default EscalationSection;
